// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";

import {
  CloseOutlined,
  ExclamationCircleOutlined,
  SaveOutlined
} from "@ant-design/icons";
import { mdiDelete, mdiPencil } from "@mdi/js";
import { Button, Popconfirm, Popover, Select, Space, Table, Tooltip } from "antd";
import styled from "styled-components";

import type {
  GeomBinNode,
  RingFenceTableRow,
  TypeWell
} from "../hooks/ring-fence/useRingFenceController";

const { Option } = Select;

interface RingFenceTableProps {
  tableData: RingFenceTableRow[];
  containerHeight: number;
  selectedGeometryBin: string;
  selectedPolygon: string;
  isLoadingRingFences: boolean;
  isLoadingGeomBin: boolean;
  isLoadingTypeWells: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  geomBinNodes: GeomBinNode[];
  typeWells: TypeWell[];
  onSave: (record: RingFenceTableRow) => void;
  onCancel: (record: RingFenceTableRow) => void;
  onEdit: (record: RingFenceTableRow) => void;
  onDelete: (record: RingFenceTableRow) => void;
  onFieldChange: (recordId: string, field: string, value: string) => void;
  getGeomBinNodeName: (nodeId: string) => string;
  getTypeWellName: (typeWellId: string) => string;
}

export default function RingFenceTable({
  tableData,
  containerHeight,
  selectedGeometryBin,
  selectedPolygon,
  isLoadingRingFences,
  isLoadingGeomBin,
  isLoadingTypeWells,
  isCreating,
  isUpdating,
  isDeleting,
  geomBinNodes,
  typeWells,
  onSave,
  onCancel,
  onEdit,
  onDelete,
  onFieldChange,
  getGeomBinNodeName,
  getTypeWellName
}: RingFenceTableProps) {
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (text: string, record: RingFenceTableRow) => {
        if (record.isEditing) {
          return (
            <input
              type="text"
              value={record.tempName || ""}
              onChange={(e) =>
                onFieldChange(record._id?.$oid || "", "name", e.target.value)
              }
              placeholder="Ring fence name"
              style={{ width: "100%" }}
            />
          );
        }
        return text || "Unnamed Ring Fence";
      }
    },
    {
      title: "Polygon",
      dataIndex: "geomBinNodeId",
      key: "geomBinNodeId",
      width: 250,
      render: (text: string, record: RingFenceTableRow) => {
        if (record.isEditing) {
          return (
            <Select
              value={record.tempGeomBinNodeId || ""}
              onChange={(value) =>
                onFieldChange(record._id?.$oid || "", "geomBinNodeId", value)
              }
              placeholder="Select polygon"
              style={{ width: "100%" }}
              showSearch
              disabled={!selectedGeometryBin}
              filterOption={(input, option) =>
                option?.children
                  ?.toString()
                  .toLowerCase()
                  .includes(input.toLowerCase()) || false
              }>
              {geomBinNodes
                .filter((node) => !node.isDeleted)
                .map((node: GeomBinNode) => (
                  <Option key={node.id} value={node.id}>
                    {node.name || `Polygon ${node.order}`}
                  </Option>
                ))}
            </Select>
          );
        }
        return text ? getGeomBinNodeName(text) : "No polygon selected";
      }
    },
    {
      title: "Type Well",
      dataIndex: "typeWellId",
      key: "typeWellId",
      width: 200,
      render: (text: string, record: RingFenceTableRow) => {
        if (record.isEditing) {
          return (
            <Select
              value={record.tempTypeWellId || ""}
              onChange={(value) =>
                onFieldChange(record._id?.$oid || "", "typeWellId", value)
              }
              placeholder="Select type well"
              style={{ width: "100%" }}
              showSearch
              loading={isLoadingTypeWells}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children
                  ?.toString()
                  .toLowerCase()
                  .includes(input.toLowerCase()) || false
              }>
              {typeWells.map((well) => (
                <Option key={well.id} value={well.id}>
                  {well.name}
                </Option>
              ))}
            </Select>
          );
        }
        return text ? getTypeWellName(text) : "Not assigned";
      }
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (text: string, record: RingFenceTableRow) => {
        if (record.isEditing) {
          const saveButton = (
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              onClick={() => onSave(record)}
              loading={isCreating || isUpdating}
              danger={!!record.error}>
              Save
            </Button>
          );

          return (
            <Space>
              {record.error ? (
                <Popover
                  content={
                    <ErrorContent>
                      <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
                      <span>{record.error}</span>
                    </ErrorContent>
                  }
                  title="Error"
                  trigger="hover"
                  open={true}
                  placement="topRight">
                  {saveButton}
                </Popover>
              ) : (
                saveButton
              )}
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => onCancel(record)}>
                Cancel
              </Button>
            </Space>
          );
        }

        return (
          <ButtonWrapper>
            <Tooltip title="Edit" overlayInnerStyle={{ padding: "10px" }}>
              <Button
                style={{ margin: "1px" }}
                type="primary"
                onClick={() => onEdit(record)}
                disabled={tableData.some((row) => row.isEditing)}
                icon={<Icon path={mdiPencil} size={1.2} />}
              />
            </Tooltip>
            <Popconfirm
              title="Are you sure you want to delete this ring fence?"
              onConfirm={() => onDelete(record)}
              okText="Yes"
              cancelText="No">
              <Tooltip title="Delete" overlayInnerStyle={{ padding: "10px" }}>
                <Button
                  style={{ margin: "1px" }}
                  type="primary"
                  danger
                  icon={<Icon path={mdiDelete} size={1.2} />}
                  loading={isDeleting}
                  disabled={tableData.some((row) => row.isEditing)}
                />
              </Tooltip>
            </Popconfirm>
          </ButtonWrapper>
        );
      }
    }
  ];

  const getEmptyText = () => {
    if (!selectedGeometryBin) {
      return "Please select a Geometry Bin first to view and manage ring fences.";
    }
    if (isLoadingGeomBin) {
      return "Loading polygons...";
    }
    if (!selectedPolygon) {
      return "Please select a Polygon to view and manage ring fences.";
    }
    return "No ring fences created yet. Click 'Add Ring Fence' to get started.";
  };
  return (
    <TableContainer height={containerHeight - 120}>
      <Table
        dataSource={tableData}
        columns={columns}
        rowKey={(record) => record._id?.$oid || ""}
        loading={isLoadingRingFences}
        pagination={false}
        size="small"
        locale={{
          emptyText: getEmptyText()
        }}
      />
    </TableContainer>
  );
}

const TableContainer = styled.div<{ height: number }>`
  height: ${(props: { height: number }) => props.height}px;
  width: 100%;
  overflow: auto;

  .ant-table-thead > tr > th {
    background-color: #fafafa;
    font-weight: 600;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f5f5f5;
  }
`;

const ErrorContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 300px;

  span {
    color: rgba(0, 0, 0, 0.85);
    line-height: 1.5;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;

  .ant-btn {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;
